import { useState, useEffect } from 'react';
import get from 'lodash.get';
import queryString from 'qs';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { makeStyles } from '@material-ui/core/styles';
import withAuthProtection from 'hoc/withAuthProtection';
import withPermissionProtection from 'hoc/withPermissionProtection';

import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Card from 'components/CncUI/Card';
import Typography from 'components/CncUI/Typography';
import Button from 'components/CncUI/Button';
import Loader from 'components/Loader';
import BasicInfo from './BasicInfo';
import Addresses from './Addresses';
import EmergencyContact from './EmergencyContact';
import DirectDeposit from './DirectDeposit';
import ErrorBoundary from 'components/ErrorBoundary';

import useSnackbar from 'hooks/useSnackbar';
import useCountry from 'hooks/useCountry';
import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';

import FmsEnums from 'constants/componentData/fmsEnums';
import parseAppReturn from './utils/parseAppReturn';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    color: palette.cncUI.primary.gray['+0'],
    paddingTop: '3em',
  },
  header: {
    marginTop: 40,
    marginBottom: 0,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  title: {
    marginTop: 0,
    marginBottom: 16,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  fixed: {
    [breakpoints.up('md')]: {
      position: 'fixed',
    },
  },
  list: {
    '& li': {
      paddingLeft: 0,
      cursor: 'pointer',
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '22px',
    },
  },
  offerLink: {
    maxWidth: 235,
    marginTop: 20,
    [breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
    [breakpoints.up('lg')]: {
      maxWidth: 300,
    },
  },
  profileContent: {
    paddingBottom: 40,
  },
}));

const Profile = props => {
  const logger = Logger('Profile');
  const { country } = useCountry();
  const classes = useStyles();
  const notify = useSnackbar();
  const { location, history } = props;
  const { search = '' } = location;
  const { oauth_state_id } = queryString.parse(search.slice(1));
  const [profile, setProfile] = useState(null);
  const [showEmergencyContact, setShowEmergencyContact] = useState(false);
  const [showDirectDeposit, setShowDirectDeposit] = useState(false);
  const [mappings, setMappings] = useState({});
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const updateProfile = useMutation('updateFullEmployeeProfile', {
    scopes: 'payroll_employee:edit',
    request: input => ({ input: { countryFlag: country, ...input } }),
    response: res => res.data.updateFullEmployeeProfile,
  });

  const getMyProfiles = useQuery('GetMyProfiles', {
    // TODO Add an error message for FMS timeouts
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    scopes: 'payroll_employee:edit',
    request: country => ({ country }),
    response: res => ({
      user: res.data.getCurrentUser,
      mappings: res.data.getProfileMappingOptions,
    }),
  });

  const removeRestriction = true;

  useEffect(() => {
    setLoading(true);
    getMyProfiles(country)
      .then(response => {
        setUser(response.user);
        setMappings(response.mappings);
      })
      .catch(err => logger.error(err))
      .finally(() => setLoading(false));
  }, [country]);

  useEffect(() => {
    if (user && user.profiles) {
      const { caps, cnc } = FmsEnums.dataSource;
      const dataSource = type => p => p.dataSource === type;
      let userProfile = user.profiles.find(dataSource(cnc));
      if (!userProfile) {
        userProfile = user.profiles.find(dataSource(caps));
      }
      if (userProfile) {
        setProfile(userProfile);
      } else {
        // sets the profile with the 1st profile in response
        setProfile(get(user, 'profiles[0]', null));
      }
      setShowEmergencyContact(true);
      setShowDirectDeposit(true);
    }
  }, [user]);

  if (loading) {
    return <Loader />;
  }

  const scrollTo = id => () => {
    const parent = document.getElementById('Home');
    const header = document.getElementById('ProfileHeader');
    const target = document.getElementById(`ProfileBox-${id}`);
    const top = target.offsetTop - header.offsetTop;
    const behavior = 'smooth';
    parent.scroll({ top, behavior });
  };

  if (profile === null) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h2" className={classes.header}>
            Profile not found
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" size="long">
            An error occurred while trying to get your profile information.{' '}
            <br />
            Please refresh this page and if the problem persist, contact our
            support team.
          </Typography>
        </Grid>
      </Grid>
    );
  }

  const update = input => {
    notify({
      type: 'info',
      message: 'Updating profile information, please wait...',
      duration: 0,
      style: { marginTop: 120 },
    });

    if (!input.basicInfo) {
      input.basicInfo = {};
    }

    input.basicInfo.idType = profile.basicInfo.idType;

    updateProfile(input)
      .then(() => {
        notify({
          type: 'success',
          message: 'Profile information updated successfully',
          duration: 4500,
          style: { marginTop: 120 },
        });
      })
      .catch(() => {
        notify({
          type: 'error',
          message: `There was an error updating your profile information.
            Please try again later or contact product support if the issue
            persists`,
          duration: 6000,
          style: { marginTop: 120 },
        });
      })
      .finally(() => {
        setLoading(true);
        history.replace('/profile');
        getMyProfiles(country)
          .then(response => {
            setUser(response.user);
            setMappings(response.mappings);
          })
          .catch(err => logger.error(err))
          .finally(() => setLoading(false));
      });
  };

  const appReturn = parseAppReturn(search);
  const offerLink = (
    <Card id="OfferLink" className={classes.offerLink}>
      <Card.Title>Back to your offer?</Card.Title>
      <Card.Content>
        <Typography variant="body2">
          Return to Start+ to continue reviewing and accepting your offer
        </Typography>
      </Card.Content>
      <Card.Actions>
        <Button href={appReturn} id="OfferLink-BackButton">
          Go Back
        </Button>
      </Card.Actions>
    </Card>
  );

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <div className={classes.fixed} id="ProfileHeader">
            <Typography variant="h2" className={classes.title}>
              My Profile
            </Typography>
            <List className={classes.list}>
              <ListItem onClick={scrollTo('BasicProfile')}>
                Basic Profile
              </ListItem>
              <ListItem onClick={scrollTo('Addresses')}>Addresses</ListItem>
              {showEmergencyContact && (
                <ListItem onClick={scrollTo('EmergencyContact')}>
                  Emergency Contact
                </ListItem>
              )}
              {showDirectDeposit && (
                <ListItem onClick={scrollTo('DirectDeposit')}>
                  Direct Deposit
                </ListItem>
              )}
            </List>
            {appReturn && offerLink}
          </div>
        </Grid>
        <Grid
          item
          md={9}
          xs={12}
          id="ProfileContent"
          className={classes.profileContent}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <BasicInfo
                profile={profile}
                mappings={mappings}
                update={update}
              />
            </Grid>
            <Grid item xs={12}>
              <Addresses profile={profile} update={update} />
            </Grid>
            {showEmergencyContact && (
              <Grid item xs={12}>
                <EmergencyContact
                  profile={profile}
                  mappings={mappings}
                  update={update}
                />
              </Grid>
            )}
            {profile && showDirectDeposit && (
              <Grid item xs={12}>
                <ErrorBoundary alert>
                  <DirectDeposit
                    profile={profile}
                    mappings={mappings}
                    update={update}
                    oAuthState={oauth_state_id}
                    removeRestriction={removeRestriction}
                  />
                </ErrorBoundary>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default compose(
  withRouter,
  withAuthProtection(),
  withPermissionProtection(['payroll_employee:edit']),
)(Profile);
